import { Controller } from "@hotwired/stimulus"
import { icon as brandIcon } from "../lib/icons"

// Connects to data-controller="journal-form"
export default class extends Controller {
  static targets = [
    "linesContainer",
    "lineTemplate",
    "emptyState",
    "balanceSummary",
    "totalDebits",
    "totalCredits",
    "balanceDifference",
    "balanceStatus",
    "balanceWarning",
    "postButton"
  ]

  connect() {
    this.lineCounter = this.linesContainerTarget.children.length
    this.updateBalance()
    this.setupEventListeners()
  }

  setupEventListeners() {
    // Listen for input changes on amount fields
    this.element.addEventListener('input', (e) => {
      if (e.target.matches('input[name*="[debit]"], input[name*="[credit]"]')) {
        this.updateBalance()
        this.clearOppositeField(e.target)
      }
    })

    // Listen for account selection changes
    this.element.addEventListener('change', (e) => {
      if (e.target.matches('select[name*="[account_id]"]')) {
        this.handleAccountChange(e.target)
      }
    })
  }

  addLine() {
    const template = this.lineTemplateTarget
    const newLineHTML = template.innerHTML.replace(/NEW_RECORD/g, new Date().getTime())

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = newLineHTML
    const newLine = tempDiv.firstElementChild

    this.linesContainerTarget.appendChild(newLine)
    this.lineCounter++
    this.updateBalance()
    this.checkEmptyState()

    // Focus the first input in the new line
    const firstInput = newLine.querySelector('select, input')
    if (firstInput) firstInput.focus()
  }

  removeLine(event) {
    const line = event.target.closest('.journal-entry-line')
    const destroyField = line.querySelector('input[name*="[_destroy]"]')

    if (destroyField) {
      destroyField.value = '1'
      line.style.display = 'none'
    } else {
      line.remove()
    }

    this.updateBalance()
    this.checkEmptyState()
  }

  clearOppositeField(targetField) {
    const line = targetField.closest('.journal-entry-line')
    if (!line) return

    const isDebit = targetField.name.includes('[debit]')
    const oppositeField = line.querySelector(
      isDebit ? 'input[name*="[credit]"]' : 'input[name*="[debit]"]'
    )

    if (oppositeField && targetField.value && parseFloat(targetField.value) > 0) {
      oppositeField.value = ''
    }
  }

  handleAccountChange(selectElement) {
    const line = selectElement.closest('.journal-entry-line')
    const debitField = line.querySelector('input[name*="[debit]"]')
    const creditField = line.querySelector('input[name*="[credit]"]')

    // Clear amounts when account changes
    if (debitField) debitField.value = ''
    if (creditField) creditField.value = ''

    // Focus appropriate field based on account type (if data available)
    const selectedOption = selectElement.selectedOptions[0]
    if (selectedOption && selectedOption.dataset.normalBalance === 'credit') {
      if (creditField) creditField.focus()
    } else if (debitField) {
      debitField.focus()
    }

    this.updateBalance()
  }

  updateBalance() {
    let totalDebits = 0
    let totalCredits = 0

    this.getVisibleLines().forEach((line) => {
      const debitField = line.querySelector('input[name*="[debit]"]')
      const creditField = line.querySelector('input[name*="[credit]"]')

      if (debitField && debitField.value) {
        totalDebits += parseFloat(debitField.value) || 0
      }

      if (creditField && creditField.value) {
        totalCredits += parseFloat(creditField.value) || 0
      }
    })

    const difference = Math.abs(totalDebits - totalCredits)
    const isBalanced = difference < 0.01 && totalDebits > 0

    // Update display
    this.totalDebitsTarget.textContent = this.formatCurrency(totalDebits)
    this.totalCreditsTarget.textContent = this.formatCurrency(totalCredits)
    this.balanceDifferenceTarget.textContent = this.formatCurrency(difference)

    // Update balance status
    this.updateBalanceStatus(isBalanced, totalDebits, totalCredits)

    // Update form controls
    this.updateFormControls(isBalanced)
  }

  updateBalanceStatus(isBalanced, totalDebits, totalCredits) {
    const statusTarget = this.balanceStatusTarget
    const differenceTarget = this.balanceDifferenceTarget

    if (totalDebits === 0 && totalCredits === 0) {
      statusTarget.className = 'badge badge-ghost'
      statusTarget.textContent = 'Sin montos'
      differenceTarget.className = 'font-mono text-lg font-bold'
    } else if (isBalanced) {
      statusTarget.className = 'badge badge-success gap-2'
      statusTarget.innerHTML = `
        ${brandIcon("check-circle", "w-4 h-4")}
        Balanceado
      `
      differenceTarget.className = 'font-mono text-lg font-bold text-success'
    } else {
      statusTarget.className = 'badge badge-error gap-2'
      statusTarget.innerHTML = `
        ${brandIcon("exclamation-triangle", "w-4 h-4")}
        Desbalanceado
      `
      differenceTarget.className = 'font-mono text-lg font-bold text-error'
    }
  }

  updateFormControls(isBalanced) {
    // Show/hide balance warning
    if (this.hasBalanceWarningTarget) {
      this.balanceWarningTarget.style.display = isBalanced ? 'none' : 'block'
    }

    // Enable/disable post button
    if (this.hasPostButtonTarget) {
      this.postButtonTarget.disabled = !isBalanced
      if (isBalanced) {
        this.postButtonTarget.classList.remove('btn-disabled')
      } else {
        this.postButtonTarget.classList.add('btn-disabled')
      }
    }
  }

  getVisibleLines() {
    return Array.from(this.linesContainerTarget.children).filter(
      line => line.style.display !== 'none' && line.classList.contains('journal-entry-line')
    )
  }

  checkEmptyState() {
    const visibleLines = this.getVisibleLines()
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.style.display = visibleLines.length === 0 ? 'block' : 'none'
    }
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
      minimumFractionDigits: 2
    }).format(amount)
  }

  postEntry() {
    if (confirm('¿Está seguro de contabilizar este asiento? No podrá editarlo después.')) {
      // Submit form and then redirect to post action
      const form = this.element
      const postUrl = form.dataset.postUrl

      if (postUrl) {
        // Create a hidden form to post the entry
        const postForm = document.createElement('form')
        postForm.method = 'POST'
        postForm.action = postUrl

        const csrfToken = document.querySelector('[name="csrf-token"]')
        if (csrfToken) {
          const csrfInput = document.createElement('input')
          csrfInput.type = 'hidden'
          csrfInput.name = 'authenticity_token'
          csrfInput.value = csrfToken.content
          postForm.appendChild(csrfInput)
        }

        const methodInput = document.createElement('input')
        methodInput.type = 'hidden'
        methodInput.name = '_method'
        methodInput.value = 'PATCH'
        postForm.appendChild(methodInput)

        document.body.appendChild(postForm)
        postForm.submit()
      }
    }
  }
}